import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, FileText, Calendar, User, Building } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerificarNotaPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verificar = async () => {
      try {
        const res = await axios.get(`${API_URL}/notas-libres/verificar/${token}`);
        setResultado(res.data);
      } catch (err) {
        setError('Error al verificar la nota');
      } finally {
        setLoading(false);
      }
    };
    
    verificar();
  }, [token]);

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    try {
      return new Date(fecha).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return fecha;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-violet-600 mb-4" />
            <p className="text-gray-600">Verificando nota...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-red-700 mb-2">Error de verificación</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <Building className="w-12 h-12 mx-auto text-violet-600 mb-2" />
          <h1 className="text-xl font-bold text-gray-800">Colegio de Trabajadores Sociales</h1>
          <p className="text-sm text-gray-500">Distrito Lomas de Zamora</p>
        </div>

        <Card className={resultado?.valido ? 'border-green-200' : 'border-red-200'}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-center gap-3">
              {resultado?.valido ? (
                <CheckCircle className="w-10 h-10 text-green-500" />
              ) : (
                <XCircle className="w-10 h-10 text-red-500" />
              )}
              <CardTitle className={resultado?.valido ? 'text-green-700' : 'text-red-700'}>
                {resultado?.valido ? 'Nota Verificada' : 'Nota No Válida'}
              </CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-lg ${resultado?.valido ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className={`text-sm ${resultado?.valido ? 'text-green-700' : 'text-red-700'}`}>
                {resultado?.mensaje}
              </p>
            </div>

            {resultado?.valido && resultado?.nota && (
              <div className="space-y-3 pt-4 border-t">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Detalles de la Nota
                </h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Nº de Nota:</span>
                    <span className="font-medium">{resultado.nota.id}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Título:</span>
                    <span className="font-medium">{resultado.nota.titulo}</span>
                  </div>
                  
                  {resultado.nota.destinatario && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-500">Destinatario:</span>
                      <span className="font-medium">{resultado.nota.destinatario}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Fecha de emisión:</span>
                    <span className="font-medium flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatearFecha(resultado.nota.fecha_creacion)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Firmante:</span>
                    <span className="font-medium">{resultado.nota.firma_nombre}</span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Cargo:</span>
                    <span className="font-medium">{resultado.nota.firma_cargo}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4 text-center">
              <Link to="/">
                <Button variant="outline" size="sm">
                  Ir al sitio principal
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-center text-gray-400 mt-6">
          Sistema de verificación de documentos del CATSLZ
        </p>
      </div>
    </div>
  );
}
